'use client'
import { __awaiter } from '../_virtual/_tslib.js';
import { getStarknet } from 'get-starknet-core';
import { Contract, cairo, constants } from 'starknet';
import { WalletConnectorBase, logger } from '@dynamic-labs/wallet-connector-core';
import { isMobile, template, DynamicError, retryableFn } from '@dynamic-labs/utils';
import { ETH_STARKNET_ADDRESS } from './constants.js';
import ETH_CONTRACT_ABI from './ethereumContractAbi.js';
import { formatTypedDataMessage } from './utils/convertors.js';

const ACCOUNT_CHANGED_EVENT_LISTENER = 'accountsChanged';
const NETWORK_CHANGED_EVENT_LISTENER = 'networkChanged';
class StarknetWalletConnector extends WalletConnectorBase {
    constructor(name, windowKey, opts) {
        var _a;
        super(opts);
        this.STARKNET_VERSION = 'v5';
        this.connectedChain = 'STARK';
        this.supportedChains = ['STARK'];
        this.switchNetworkOnlyFromWallet = true;
        // required for metamask snap integration as MM snaps don't have event listeners
        this.canSetEventListeners = true;
        this.name = name;
        this.windowKey = windowKey;
        this.starknetNetworks = opts.starknetNetworks;
        this.chainRpcProviders = opts.chainRpcProviders;
        (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.registerStarknetProviders();
    }
    get walletBookWallet() {
        return this.walletBook.wallets[this.key];
    }
    // abstracting this method to a function due to some wallets (bitget)
    // having non-standard use of the isConnected flag. This allows for us to use other flags to determine connectivity
    isProviderConnected() {
        var _a;
        const wallet = this.getWallet();
        return (_a = wallet === null || wallet === void 0 ? void 0 : wallet.isConnected) !== null && _a !== void 0 ? _a : false;
    }
    getPublicClient() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.starknetNetworks === undefined ||
                this.starknetNetworks.length === 0)
                return;
            const configurations = {
                cosmos: [],
                evm: undefined,
                solana: undefined,
                starknet: this.starknetNetworks,
            };
            const chainId = yield this.getNetwork();
            const starknetRpcProvider = (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.getStarknetRpcProviderByChainId(configurations, chainId);
            return starknetRpcProvider === null || starknetRpcProvider === void 0 ? void 0 : starknetRpcProvider.provider;
        });
    }
    /**
     * returns starknet wallet network id
     */
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const wallet = this.getWallet();
            if (!wallet) {
                return Promise.resolve(undefined);
            }
            if (!this.isProviderConnected()) {
                yield wallet.enable({ starknetVersion: this.STARKNET_VERSION });
            }
            return Promise.resolve((_a = wallet.provider) === null || _a === void 0 ? void 0 : _a.getChainId());
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            try {
                if (wallet && !this.isProviderConnected()) {
                    yield wallet.enable({ starknetVersion: this.STARKNET_VERSION });
                    localStorage.setItem('dynamic_should_have_wallet', 'true');
                }
            }
            catch (err) {
                logger.error(err);
            }
        });
    }
    getAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            if (!this.isInstalledOnBrowser() &&
                isMobile() &&
                this.walletBookWallet.mobile &&
                this.walletBookWallet.mobile.inAppBrowser) {
                const inAppBrowserCompiledTemplate = template(this.walletBookWallet.mobile.inAppBrowser);
                const deepLink = inAppBrowserCompiledTemplate({
                    encodedDappURI: encodeURIComponent(window.location.toString()),
                });
                window.location.assign(deepLink);
            }
            if (wallet && !this.isProviderConnected()) {
                yield (wallet === null || wallet === void 0 ? void 0 : wallet.isPreauthorized());
                yield this.connect();
            }
            return Promise.resolve(wallet === null || wallet === void 0 ? void 0 : wallet.selectedAddress);
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            return wallet === null || wallet === void 0 ? void 0 : wallet.account;
        });
    }
    getWalletClient() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            return wallet === null || wallet === void 0 ? void 0 : wallet.provider;
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            // FetchPublicAddress also handles the connection to the wallet this is why we call it before getWallet.
            const walletAddress = yield this.getAddress();
            const wallet = this.getWallet();
            if (!walletAddress || !wallet) {
                logger.error('Could not fetch wallet address for signing message');
                return undefined;
            }
            const encodedMessage = formatTypedDataMessage(messageToSign, (yield wallet.provider.getChainId()));
            const signature = yield wallet.account.signMessage(encodedMessage);
            return signature === null || signature === void 0 ? void 0 : signature.join(',');
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const walletAddress = yield this.getAddress();
            const provider = yield this.getWalletClient();
            if (!walletAddress || !provider) {
                logger.error('Could not fetch wallet address for getting balance');
                return undefined;
            }
            const contract = new Contract(ETH_CONTRACT_ABI, ETH_STARKNET_ADDRESS, provider);
            try {
                const { balance } = yield contract.balanceOf(walletAddress);
                /**
                 * Dividing by 1e18 as the returned balance is a Gwei number.
                 * Read more here: https://www.investopedia.com/terms/g/gwei-ethereum.asp#toc-what-is-gwei
                 */
                return (Number(cairo.uint256(balance.low).low) / 1e18).toFixed(6);
            }
            catch (error) {
                logger.error(error);
                throw new DynamicError('Something went wrong');
            }
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem('dynamic_should_have_wallet');
            getStarknet().disconnect({ clearLastWallet: true });
        });
    }
    getWallet() {
        let starknetWindowInstance = window[`starknet_${this.windowKey}`];
        if (starknetWindowInstance !== undefined) {
            return starknetWindowInstance;
        }
        else {
            starknetWindowInstance = window['starknet'];
            if ((starknetWindowInstance === null || starknetWindowInstance === void 0 ? void 0 : starknetWindowInstance.name) === this.name) {
                return starknetWindowInstance;
            }
        }
        return undefined;
    }
    isInstalledOnBrowser() {
        return this.getWallet() !== undefined;
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            if (!wallet) {
                return [];
            }
            try {
                yield this.reconnectIfNeeded(wallet);
            }
            catch (e) {
                return [];
            }
            const getSelectedAddress = () => wallet.selectedAddress
                ? Promise.resolve([wallet.selectedAddress])
                : Promise.reject();
            return retryableFn(getSelectedAddress, {
                fallbackValue: [],
                retryIntervalMs: 100,
                retryStrategy: 'timeout-and-rejection',
            });
        });
    }
    reconnectIfNeeded(wallet) {
        return __awaiter(this, void 0, void 0, function* () {
            const needsReconnection = !this.isProviderConnected() && (yield wallet.isPreauthorized());
            if (needsReconnection) {
                /**
                 * this is a hack for braavos
                 * it is needed because the wallet doesn't return any addresses or connected state
                 * without a reconnection, and when the wallet is locked, the connect call never resolves
                 */
                if (localStorage.getItem('dynamic_should_have_wallet') === 'true') {
                    try {
                        yield retryableFn(this.connect.bind(this), {
                            maxRetries: 0,
                            retryStrategy: 'timeout-only',
                            timeoutMs: 1000,
                        });
                    }
                    catch (_a) {
                        localStorage.removeItem('dynamic_should_have_wallet');
                        throw new Error('Could not reconnect');
                    }
                }
                else {
                    yield this.connect();
                }
            }
        });
    }
    setupEventListeners() {
        if (!this.canSetEventListeners)
            return;
        const wallet = this.getWallet();
        if (!wallet) {
            return logger.error('Wallet has not been found');
        }
        this.handleAccountChange = (address) => __awaiter(this, void 0, void 0, function* () {
            if (!address) {
                this.emit('disconnect');
                return;
            }
            if (address.toString()) {
                this.emit('accountChange', { accounts: [address.toString()] });
            }
        });
        this.handleNetworkChange = (network) => __awaiter(this, void 0, void 0, function* () {
            if (!network) {
                this.emit('disconnect');
                return;
            }
            const chain = this.mapNetworkNameToChainId(network.toString());
            if (chain) {
                this.emit('chainChange', { chain });
            }
        });
        wallet === null || wallet === void 0 ? void 0 : wallet.on(ACCOUNT_CHANGED_EVENT_LISTENER, this.handleAccountChange);
        wallet === null || wallet === void 0 ? void 0 : wallet.on(NETWORK_CHANGED_EVENT_LISTENER, this.handleNetworkChange);
    }
    teardownEventListeners() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.canSetEventListeners)
                return;
            const wallet = this.getWallet();
            if (this.handleAccountChange) {
                wallet === null || wallet === void 0 ? void 0 : wallet.off(ACCOUNT_CHANGED_EVENT_LISTENER, this.handleAccountChange);
                this.handleAccountChange = undefined;
            }
            if (this.handleNetworkChange) {
                wallet === null || wallet === void 0 ? void 0 : wallet.off(NETWORK_CHANGED_EVENT_LISTENER, this.handleNetworkChange);
                this.handleNetworkChange = undefined;
            }
        });
    }
    mapNetworkNameToChainId(networkNameOrChainId) {
        if (networkNameOrChainId === constants.NetworkName.SN_MAIN ||
            networkNameOrChainId.includes('mainnet') ||
            networkNameOrChainId === constants.StarknetChainId.SN_MAIN) {
            return constants.StarknetChainId.SN_MAIN;
        }
        else if (networkNameOrChainId === constants.NetworkName.SN_GOERLI ||
            networkNameOrChainId.includes('goerli') ||
            networkNameOrChainId === constants.StarknetChainId.SN_GOERLI) {
            return constants.StarknetChainId.SN_GOERLI;
        }
        else if (networkNameOrChainId === constants.NetworkName.SN_SEPOLIA ||
            networkNameOrChainId.includes('sepolia') ||
            networkNameOrChainId === constants.StarknetChainId.SN_SEPOLIA) {
            return constants.StarknetChainId.SN_SEPOLIA;
        }
        else {
            logger.error('Unknown networkName', networkNameOrChainId);
            return undefined;
        }
    }
    mapChainIdToNetworkName(chainIdOrNetworkName) {
        if (chainIdOrNetworkName === constants.StarknetChainId.SN_MAIN ||
            chainIdOrNetworkName === constants.NetworkName.SN_MAIN) {
            return constants.NetworkName.SN_MAIN;
        }
        else if (chainIdOrNetworkName === constants.StarknetChainId.SN_GOERLI ||
            chainIdOrNetworkName === constants.NetworkName.SN_GOERLI) {
            return constants.NetworkName.SN_GOERLI;
        }
        else if (chainIdOrNetworkName === constants.StarknetChainId.SN_SEPOLIA ||
            chainIdOrNetworkName === constants.NetworkName.SN_SEPOLIA) {
            return constants.NetworkName.SN_SEPOLIA;
        }
        else {
            logger.error('Unknown chainId', chainIdOrNetworkName);
            return undefined;
        }
    }
}

export { ACCOUNT_CHANGED_EVENT_LISTENER, NETWORK_CHANGED_EVENT_LISTENER, StarknetWalletConnector as default };
